// WSPulse local device agent client.
// Talks to the local agent over plain fetch (NOT axios) so the axios request
// interceptor does not attach headers to the agent call and cause recursion.

const AGENT_URL = "http://127.0.0.1:7379/token";

// In-memory only. Never persisted so it can't be replayed across sessions.
let cachedToken = null;

/**
 * Fetch a fresh signed token from the local WSPulse agent.
 * @returns {Promise<{mac:string, ts:number, sig:string}|null>}
 */
export async function fetchAgentToken() {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 3000);
  try {
    const res = await fetch(AGENT_URL, {
      method: "GET",
      signal: controller.signal,
      // Agent serves CORS * so this works from any origin, including inside an iframe.
      cache: "no-store",
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data || !data.mac || !data.ts || !data.sig) return null;
    cachedToken = {
      mac: data.mac,
      ts: Number(data.ts),
      sig: data.sig,
    };
    return cachedToken;
  } catch (err) {
    // Timeout, network error, agent not running, bad JSON, etc.
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * @returns {{mac:string, ts:number, sig:string}|null} the last cached token, if any.
 */
export function getCachedToken() {
  return cachedToken;
}

/**
 * Reuse the cached token if it is still fresh (< 2 min old, well within the
 * server's 5-min skew window), otherwise fetch a new one.
 * @returns {Promise<{mac:string, ts:number, sig:string}|null>}
 */
export async function getFreshToken() {
  if (cachedToken && Date.now() - cachedToken.ts < 120000) {
    return cachedToken;
  }
  return fetchAgentToken();
}
